import { createStyles } from 'antd-style'

export const ASIDE_INSET = 18

export const useStyles = createStyles(({ token, responsive }) => ({
  /** Edge-to-edge two-pane stage, no cards. */
  split: {
    minHeight: '100dvh',
    width: '100%',
    display: 'grid',
    // 52.5/47.5 two-pane split on lg+ (where the aside renders); `fr` keeps the
    // ratio after the gap. antd-style `responsive.*` are max-width queries, so
    // `responsive.md` (≤991px) is the "below lg" branch — it must match the
    // aside's `screens.lg` (≥992px) render gate. `responsive.lg` would be
    // ≤1199px, leaving a 992–1199px window where the grid is single-column but
    // the aside still renders and stacks under the content.
    gridTemplateColumns: '52.5fr 47.5fr',
    alignItems: 'stretch',
    backgroundColor: token.colorBgContainer,
    gap: 12,

    [responsive.md]: {
      gridTemplateColumns: '1fr'
    }
  },

  /** Single-pane mode: nothing to put beside the content. */
  splitSolo: {
    gridTemplateColumns: '1fr'
  },

  /** Left — flat light pane: brand top-left, content centred, footer bottom. */
  contentPane: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: '24px 24px',
    backgroundColor: token.colorBgContainer,

    [responsive.md]: {
      padding: '28px 48px'
    }
  },

  /** Brand row, pinned to the top of the content pane. */
  brandRow: {
    flexShrink: 0
  },

  brandLogoLink: {
    display: 'inline-block',
    maxWidth: 150,
    lineHeight: 0
  },

  /** Vertically centres the content between brand and footer. */
  contentCenter: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBlock: 16
  },

  contentInner: {
    width: '100%',

    // Darker, slightly heavier field labels on this surface.
    '.ant-form-item-label > label': {
      color: token.colorText,
      fontWeight: 500
    }
  },

  /** Legal footer, bottom-left. */
  footerRow: {
    flexShrink: 0,
    rowGap: 4
  },

  footerControls: {
    flexShrink: 0
  },

  footerLinks: {
    minWidth: 0,

    '.ant-typography': {
      textDecorationLine: 'none'
    },

    '.ant-typography:hover, .ant-typography:focus-visible': {
      textDecorationLine: 'underline',
      textUnderlineOffset: 3
    }
  },

  /**
   * Right pane (lg+ only). One rounded, inset slab whatever goes in it — the
   * geometry is what the two surfaces share, so it lives here and the fill is
   * the caller's business.
   */
  asidePane: {
    margin: `${ASIDE_INSET}px ${ASIDE_INSET}px ${ASIDE_INSET}px 0`,
    // Sticky and capped to the viewport: the grid's height follows the *left*
    // column, so a right pane that only said `100%` grew with whatever it
    // contained. A build with forty pipeline steps then pushed the panel off the
    // bottom of the screen instead of scrolling inside itself.
    position: 'sticky',
    top: ASIDE_INSET,
    alignSelf: 'start',
    height: `calc(100dvh - ${ASIDE_INSET * 2}px)`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: 24,
    minWidth: 0
  },

  /** The immersive dark fill both surfaces sit on. */
  asideBrand: {
    gap: 32,
    // Narrower at the sides than at the top: what sits in the slab (the auth
    // hero, the workspace preview) wants the width, while the top inset keeps
    // content clear of the absolutely-positioned wordmark.
    padding: '48px 32px',
    boxShadow:
      '0 14px 54px color-mix(in srgb, #0B1420 24%, transparent), 0 8px 27px color-mix(in srgb, #0B1420 15%, transparent)',
    color: token.colorWhite,
    backgroundImage:
      'linear-gradient(155deg, rgba(11,20,32,0.86) 0%, rgba(11,20,32,0.6) 45%, rgba(15,118,110,0.5) 100%), url(/images/hero-auth.png)',
    backgroundSize: 'cover',
    backgroundColor: token.colorBgContainer,
    backgroundPosition: 'center',

    // 992–1199px: the slab renders (`screens.lg`) but is barely 460px wide, so
    // the inset is a fifth of it. Trimmed rather than dropped — the workspace
    // preview is scaled to this pane, and what the padding costs comes
    // straight out of the mock's legibility.
    [responsive.lg]: {
      padding: '40px 20px'
    }
  },

  /** Brand mark in the top-left of the dark pane (white). */
  asideWordmark: {
    position: 'absolute',
    top: 24,
    left: 32,
    maxHeight: 20,

    // In lockstep with `asideBrand`'s inset: absolutely positioned, so it does
    // not follow the padding on its own and would sit out of line with the
    // content beneath it.
    [responsive.lg]: {
      left: 20
    }
  }
}))
